import { z } from 'zod';
export var SecurityLevel;
(function (SecurityLevel) {
    SecurityLevel["PUBLIC"] = "public";
    SecurityLevel["INTERNAL"] = "internal";
    SecurityLevel["CONFIDENTIAL"] = "confidential";
    SecurityLevel["RESTRICTED"] = "restricted";
})(SecurityLevel || (SecurityLevel = {}));
/**
 * Whether an item's detail may be exposed publicly — the single decision point for the
 * public-vs-title-only split (GOV-1476). Only `public` items expose their summary, recommended
 * action, fiscal note, and attachments; every other level is title-only. Shared so the public
 * agenda builder (`api-public`) and the meeting-script worker (`queue-service`) apply one rule.
 *
 * @param securityLevel - The item's `securityLevel` (accepts the raw string from either the
 *   validation or Prisma `SecurityLevel` enum).
 * @returns `true` when the level is `public`.
 */
export const isPublicItem = (securityLevel) => securityLevel === SecurityLevel.PUBLIC;
export var GeneratedDocumentsStatus;
(function (GeneratedDocumentsStatus) {
    GeneratedDocumentsStatus["Pending"] = "pending";
    GeneratedDocumentsStatus["Failed"] = "failed";
    GeneratedDocumentsStatus["Completed"] = "completed";
})(GeneratedDocumentsStatus || (GeneratedDocumentsStatus = {}));
/**
 * Numbering styles shared by outline numbering (agenda items) and attachment numbering.
 * Maps to the styles understood by `generateNumber` in `@govinity/outline-numbering-utils`.
 */
export const numberingStyleSchema = z.enum([
    'none',
    '1,2,3',
    'a,b,c',
    'A,B,C',
    'i,ii,iii',
    'I,II,III',
]);
export const paginationSchema = z.object({
    limit: z
        .union([z.number().int().nonnegative(), z.string().transform((val) => parseInt(val, 10))])
        .optional(),
    offset: z
        .union([z.number().int().nonnegative(), z.string().transform((val) => parseInt(val, 10))])
        .optional(),
});
/**
 * Pagination schema with sorting, where `orderBy` is restricted to an allowlist of sortable fields.
 * `orderBy` becomes a Prisma `orderBy` key, so free-form values would 500 on unknown
 * fields and expose ordering on fields the caller can't otherwise see.
 */
export function createSortablePaginationSchema(orderByFields) {
    return paginationSchema.extend({
        orderBy: z.enum(orderByFields).optional(),
        orderDirection: z.enum(['asc', 'desc']).optional(),
    });
}
/**
 * Creates a flexible String filter schema that can accept either strings or enum values
 * @param enumSchema - Optional enum schema to use instead of plain string
 * @returns A Zod schema for String-based filters
 */
export function createStringFilterSchema(enumSchema = z.string()) {
    return z.union([
        enumSchema,
        z.object({
            in: z.array(enumSchema).min(1),
        }),
        z.object({
            not: enumSchema,
        }),
        z.object({
            notIn: z.array(enumSchema).min(1),
        }),
    ]);
}
/**
 * Pre-configured string-based ID filter schema for common use cases
 */
export const stringIdFilterSchema = createStringFilterSchema();
/**
 * Positive integer schema that accepts number or numeric string.
 * MCP and other clients may send IDs as strings; coercing ensures validation succeeds.
 */
export const positiveIntSchema = z.union([
    z.number().int().positive(),
    z
        .string()
        .transform((s) => parseInt(s, 10))
        .refine((n) => Number.isInteger(n) && n > 0, { message: 'Must be a positive integer' }),
]);
export const positiveIdSchema = z.number().int().positive();
export const nullablePositiveIdSchema = positiveIdSchema.nullable();
/**
 * Validates payloads where at least one mutable field is required.
 */
export const withAtLeastOneField = (schema, fields) => {
    return schema.refine((data) => fields.some((field) => data[field] !== undefined), 'At least one field must be provided');
};
/**
 * Schema for ID-based filters with support for simple values, arrays, negation, and null
 */
export const idFilterSchema = z.union([
    positiveIntSchema,
    z.null(),
    z.object({
        in: z.array(positiveIntSchema).min(1),
    }),
    z.object({
        not: positiveIntSchema,
    }),
    z.object({
        notIn: z.array(positiveIntSchema).min(1),
    }),
]);
/**
 * Schema for date-based filters with support for ranges, equality, and negation
 */
export const dateFilterSchema = z.union([
    z.string().datetime(),
    z
        .object({
        gte: z.string().datetime().optional(),
        lte: z.string().datetime().optional(),
    })
        .refine((data) => data.gte !== undefined || data.lte !== undefined, {
        message: "At least one of 'gte' or 'lte' must be provided",
    }),
    z.object({
        not: z.union([
            z.string().datetime(),
            z
                .object({
                gte: z.string().datetime().optional(),
                lte: z.string().datetime().optional(),
            })
                .refine((data) => data.gte !== undefined || data.lte !== undefined, {
                message: "At least one of 'gte' or 'lte' must be provided when using 'not' with range",
            }),
        ]),
    }),
]);
/**
 * Schema for a number field that can be initially null but must be a positive integer on submission.
 * The inferred type is `number | null` to allow null in form state, but validation requires a positive integer.
 * @param message - The error message to display when the value is null or invalid
 */
export const initiallyNullNumber = (message = 'Required') => {
    const schema = z.number().int().positive(message).nullable();
    return schema.superRefine((val, ctx) => {
        if (val === null) {
            ctx.addIssue({
                code: 'custom',
                message,
            });
        }
    });
};
